import {
    ActionMenu,
    ButtonSecondary,
    Card,
    Colors,
    Heading,
    Icon,
    Pills,
    Text,
    type ColorType,
    type OptionProps,
    type PillsVariant,
    type IconName,
} from '@amelio/ui-library';

import { ReviewTemplateStatus } from '@/constants/review/ReviewTemplateStatus';
import { useAmelioTranslations } from '@/hooks';

export type TemplateCardProps = {
    iconName: IconName;
    iconColor?: ColorType | 'inherit' | undefined;
    title: string;
    description: string;
    questionCount?: number;
    /** The pill's text, for a template that is not counted in questions; wins over `questionCount`. */
    countLabel?: string;
    status?: ReviewTemplateStatus;
    actions?: OptionProps[];
    isSelected?: boolean;
    onCardClick?: () => void;
    onButtonClick?: () => void;
    onActionItemClick?: (id: string) => void;
    className?: string;
    buttonText?: string;
};

export const TemplateCard = ({
    iconName,
    iconColor = Colors.SECONDARY,
    title,
    description,
    questionCount,
    countLabel,
    status,
    actions,
    isSelected,
    onCardClick,
    onButtonClick,
    onActionItemClick,
    className,
    buttonText,
}: TemplateCardProps) => {
    const { t } = useAmelioTranslations();

    const getQuestionLabel = (count: number): string => {
        const translationKey = count === 1 ? 'common.question' : 'common.questions';
        return t(translationKey).toLowerCase();
    };

    const getPillLabel = (): string | undefined => {
        if (countLabel !== undefined) return countLabel;
        if (questionCount === undefined) return undefined;
        return `${questionCount} ${getQuestionLabel(questionCount)}`;
    };

    const pillLabel = getPillLabel();

    const getStatusVariant = (): PillsVariant => {
        if (status === ReviewTemplateStatus.ACTIVE) return 'activeLight';
        if (status === ReviewTemplateStatus.INACTIVE) return 'inactiveLight';
        if (status === ReviewTemplateStatus.DRAFT) return 'draftLight';
        return 'lightPurple';
    };

    const getStatusText = (): string => {
        if (status === ReviewTemplateStatus.ACTIVE) return t('common.active');
        if (status === ReviewTemplateStatus.INACTIVE) return t('common.inactive');
        if (status === ReviewTemplateStatus.DRAFT) return t('common.draft');
        return '';
    };

    return (
        <Card
            bgColor={'bg-white/70'}
            borderRadius={'2xl'}
            className={`cursor-pointer border-2 hover:shadow-xl ${className} !min-h-60 md:!min-h-80`}
            onClick={onCardClick}
            borderColor={isSelected ? Colors.PRIMARY : Colors.WHITE}
        >
            <div className={'grow'}>
                <div className={'flex justify-between'}>
                    <div className={'bg-secondary-50 w-fit rounded-md p-3'}>
                        <Icon
                            name={iconName}
                            size={32}
                            color={iconColor}
                        />
                    </div>

                    <div onClick={(event) => event.stopPropagation()}>
                        {actions && actions.length > 0 && (
                            <ActionMenu
                                items={actions}
                                size={'sm'}
                                onItemClick={(id) => onActionItemClick?.(id)}
                            />
                        )}
                    </div>
                </div>

                <div>
                    <Heading
                        level={6}
                        className={'mt-4'}
                    >
                        {title}
                    </Heading>

                    <Text
                        className={'mt-2'}
                        size={'small'}
                    >
                        {description}
                    </Text>
                </div>
            </div>

            <div className={'mt-6 flex w-full items-center justify-between'}>
                {pillLabel !== undefined && (
                    <Pills
                        variant={'purple'}
                        className={'px-6 text-center text-xs'}
                    >
                        {pillLabel}
                    </Pills>
                )}

                {status ? (
                    <Pills
                        variant={getStatusVariant()}
                        className={'px-6 text-center text-xs'}
                    >
                        {getStatusText()}
                    </Pills>
                ) : (
                    <ButtonSecondary
                        size={'sm'}
                        className={'border! px-6 py-2'}
                        onClick={
                            onButtonClick
                                ? (event) => {
                                      event.stopPropagation();
                                      onButtonClick();
                                  }
                                : undefined
                        }
                    >
                        <Text
                            align={'center'}
                            className={'text-xs'}
                            weight={'medium'}
                        >
                            {buttonText || t('evaluation360SelectTemplate.useTemplate')}
                        </Text>
                    </ButtonSecondary>
                )}
            </div>
        </Card>
    );
};
